import I18n from 'I18n';
import getLang from 'I18n/utils/getLang';
import getLangLocale from 'I18n/utils/getLangLocale';
import Loadable from 'UIComponents/decorators/Loadable';
import http from 'hub-http/clients/apiClient';
import Raven from 'raven-js';
import { memo, useCallback, useEffect, useState } from 'react';
import { useReactNodeView } from '../utils/ReactNodeView';
import { I18N_LINK_TEXT_KEY_BASE, LINK_TOKEN, nullUnsubscribeLinkType, unsubscribeLinkTypes } from './unsubscribeLink/UnsubscribeConstants';
import { jsx as _jsx } from "react/jsx-runtime";
const UnsubscribeEdit = Loadable({
  loader: () => import('./unsubscribeLink/UnsubscribeEdit').then(module => module.default),
  LoadingComponent: () => null
});
const NULL_VIEW_MODEL = {
  loaded: false,
  locale: null,
  language: null,
  unsubscribeLinkType: nullUnsubscribeLinkType.NO_UNSUBSCRIBE_LINK
};

//Type Predicate bridges the two unsubscribeLinkType types:
//the User Preferences response (broad) and the UnsubscribeEdit Props (narrow)
const unsubscribeLinkTypeIsReadyToRender = link => {
  const isInTheNarrowEnum = Object.values(unsubscribeLinkTypes).includes(link);
  return isInTheNarrowEnum;
};

// Document how the "ready" state does not propagate nulls
// and narrows the unsubscribe link type
// language is still nullable, default provided in UnsubscribeEdit not required for render
const inReadyToRenderState = state => {
  const {
    locale,
    unsubscribeLinkType,
    loaded
  } = state;
  return loaded && !!locale && unsubscribeLinkTypeIsReadyToRender(unsubscribeLinkType);
};

// if not for memoization, UnsubscribeEdit would rerender a dozen times on page load and then on every keystroke
const MemoizedUnsubscribeEdit = /*#__PURE__*/memo(UnsubscribeEdit, (prevProps, nextProps) => {
  return prevProps.unsubscribeData.linkType === nextProps.unsubscribeData.linkType && prevProps.unsubscribeData.locale === nextProps.unsubscribeData.locale;
});
const nodeAttributes = ({
  linkType,
  locale,
  language
}) => {
  const linkUrlText = I18n.text('hubspot-prosemirror.unsubscribeLink.link', {
    locale
  });
  const link = /*#__PURE__*/_jsx("a", {
    href: LINK_TOKEN,
    children: linkUrlText
  });
  const linkText = I18n.text(`${I18N_LINK_TEXT_KEY_BASE}${linkType}.TO_EMAIL`, {
    locale,
    link
  });
  return {
    linkType,
    locale,
    language,
    linkText,
    linkUrlText
  };
};
export const readyToTransact = transactionTools => {
  const {
    view,
    getPos
  } = transactionTools;
  return !!view && !!getPos && typeof getPos === 'function';
};
export const nodeAttributesReady = attributes => {
  const {
    linkType,
    locale,
    language,
    linkText,
    linkUrlText
  } = attributes;
  return !!linkType && !!locale && !!language && !!linkText && !!linkUrlText;
};
export const nodeAttributesDifferFromViewModel = (attributes, viewModel) => {
  return attributes.linkType !== viewModel.unsubscribeLinkType && attributes.locale !== viewModel.locale && attributes.language !== viewModel.language;
};
export default function Unsubscribe() {
  const [viewModel, setViewModel] = useState(NULL_VIEW_MODEL);
  const context = useReactNodeView();
  const {
    view,
    getPos,
    node
  } = context;

  /*
    State management loop Effect:
    1) If there's state from node attributes, restore it
    2) If there's none,
      - fetch core parameters from the middleware data service
      - generate node attributes from core parameters
      - update the node attributes, so (1) will pick it up next rerender
  */
  useEffect(() => {
    // it's a "context" that's only needed
    // to make type narrowing through control flow work
    const transactionTools = {
      view,
      getPos
    };
    const nodeAttrs = context.node ? context.node.attrs : {};
    if (nodeAttributesReady(nodeAttrs) && nodeAttributesDifferFromViewModel(nodeAttrs, viewModel)) {
      const readyViewModel = {
        loaded: true,
        locale: nodeAttrs.locale,
        language: nodeAttrs.language,
        unsubscribeLinkType: nodeAttrs.linkType
      };
      setViewModel(readyViewModel);
    } else if (!nodeAttributesReady(nodeAttrs) && !inReadyToRenderState(viewModel)) {
      http.get(`cursus/settings/unsubscribe-links`).then(({
        unsubscribeLinkType
      }) => {
        // if the user set NO_UNSUBSCRIBE_LINK, we don't need to do anything
        const locale = getLangLocale();
        const lang = getLang();
        if (unsubscribeLinkType === nullUnsubscribeLinkType.NO_UNSUBSCRIBE_LINK) {
          setViewModel(NULL_VIEW_MODEL);
        } else if (locale && lang && unsubscribeLinkTypeIsReadyToRender(unsubscribeLinkType) && readyToTransact(transactionTools)) {
          const newAttributes = nodeAttributes({
            linkType: unsubscribeLinkType,
            locale,
            language: lang
          });
          const attributeUpdate = transactionTools.view.state.tr.setNodeMarkup(transactionTools.getPos(), undefined, newAttributes);
          transactionTools.view.dispatch(attributeUpdate);
        } else {
          let stringifiedResponse;
          try {
            stringifiedResponse = JSON.stringify({
              unsubscribeLinkType
            });
          } catch (ex) {
            stringifiedResponse = 'Could not stringify response';
          }
          throw new Error('Strange Unsubscribe state', {
            cause: {
              networkResponse: stringifiedResponse,
              readyToTransact: readyToTransact(transactionTools)
            }
          });
        }
      }).catch(error => {
        Raven.captureException(error, {
          extra: error.cause
        });
        setViewModel(NULL_VIEW_MODEL);
      });
    }
  }, [view, getPos, context.node, viewModel, viewModel.loaded, viewModel.language, viewModel.locale, viewModel.unsubscribeLinkType]);
  const onConfirm = useCallback(unsubscribeData => {
    const attrs = !!node && node.attrs;
    const transactionTools = {
      view,
      getPos
    };
    if (readyToTransact(transactionTools) && !!attrs) {
      const newAttributes = nodeAttributes({
        linkType: unsubscribeData.linkType,
        locale: unsubscribeData.locale,
        language: unsubscribeData.language
      });
      const attributeUpdate = transactionTools.view.state.tr.setNodeMarkup(transactionTools.getPos(), undefined, newAttributes);
      transactionTools.view.dispatch(attributeUpdate);
    }
  }, [node, view, getPos]);
  if (inReadyToRenderState(viewModel)) {
    const {
      locale,
      language,
      unsubscribeLinkType
    } = viewModel;
    const unsubscribeEditProps = {
      unsubscribeData: {
        open: false,
        locale,
        selectedLang: language,
        linkType: unsubscribeLinkType
      },
      onConfirm
    };
    return /*#__PURE__*/_jsx(MemoizedUnsubscribeEdit, Object.assign({}, unsubscribeEditProps));
  } else {
    return /*#__PURE__*/_jsx("div", {});
  }
}
export const unsubscribeViewUpdateFn = ({
  previousNode,
  node
}) => {
  if (previousNode.attrs.linkType !== node.attrs.linkType || previousNode.attrs.locale !== node.attrs.locale || previousNode.attrs.language !== node.attrs.language || previousNode.attrs.linkText !== node.attrs.linkText || previousNode.attrs.linkUrl !== node.attrs.linkUrl || previousNode.attrs.linkUrlText !== node.attrs.linkUrl) {
    return false;
  }
  return true;
};